import React from 'react';
import { FormContext } from 'react-hook-form';
import { FlatList, Platform } from 'react-native';
import { IconButton } from 'react-native-paper';
import { useSafeArea } from 'react-native-safe-area-context';

import Container from '@src/components/Container';
import KeyboardAvoidingView from '@src/components/KeyboardAvoidingView';
import PostCard from '@src/components/PostCard';
import RefreshControl from '@src/components/RefreshControl';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { idKeyExtractor } from '@src/configuration/constants';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import usePostComments from '@src/hooks/usePostComments';
import { Comment } from '@src/types';

import CommentCard from './CommentCard';
import { CommentInput, CommentInputContainer, Separator } from './styled';
import styles from './styles';
import { Props } from './types';
import useCommentForm from './useCommentForm';
import usePost from './usePost';

const Screen: React.FC<Props> = ({ route }) => {
  const handleScroll = useAnimatedHeader();
  const id = route.params.id;
  const post = usePost(id);
  const { handleEndReached, data, refreshing, handleRefresh } = usePostComments(
    id
  );
  const { form, handleSubmit, loading } = useCommentForm(id);
  const insets = useSafeArea();

  const handleRefreshAfterSubmit = React.useCallback(async () => {
    try {
      await handleSubmit();
      await handleRefresh();
    } catch (e) {
      console.log(e);
    }
  }, [handleRefresh, handleSubmit]);

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <FlatList
          onScroll={handleScroll}
          ListHeaderComponent={<PostCard play extended data={post.data} />}
          renderItem={({ item }: { item: Comment }) => (
            <CommentCard data={item} />
          )}
          keyExtractor={idKeyExtractor}
          ItemSeparatorComponent={Separator}
          data={data}
          contentContainerStyle={styles.contentContainer}
          onEndReached={handleEndReached}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        />
        <FormContext {...form}>
          <KeyboardAvoidingView
            keyboardVerticalOffset={
              (Platform.OS === 'ios' ? 64 + insets.bottom : undefined) as any
            }
          >
            <CommentInputContainer>
              <CommentInput name="content" disabled={loading} />
              <IconButton
                icon="paper-plane-o-rtl"
                onPress={handleRefreshAfterSubmit}
                disabled={loading}
              />
            </CommentInputContainer>
          </KeyboardAvoidingView>
        </FormContext>
      </Container>
    </>
  );
};

Screen.displayName = 'Post';

export default Screen;
